import React, { useState, useEffect, useRef } from 'react';

import { get } from '../../api';
import { SENTINEL_TMA } from '../../constants/sentinel_types';

import Frame from '../../components/Frame';
import FrameHeader from '../../components/FrameHeader';
import LoadingFrame from '../../components/LoadingFrame';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import AdminPortalTmaReportSummary from '../AdminPortalTmaReportSummary';

import styles from './AdminPortalTmaReport.module.scss';

function useDebounce(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

const DATE_RANGE_OPTIONS = [
    { key: 'current_week', label: 'Current week' },
    { key: 'last_week', label: 'Last week' },
    { key: 'current_month', label: 'Current month' },
    { key: 'last_month', label: 'Last month' },
];

function getDateRange(key) {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();

    // week starts Monday
    const dayOfWeek = (now.getDay() + 6) % 7; // Mon=0 … Sun=6

    switch (key) {
        case 'current_week': {
            const from = new Date(now);
            from.setDate(now.getDate() - dayOfWeek);
            from.setHours(0, 0, 0, 0);
            const to = new Date(from);
            to.setDate(from.getDate() + 6);
            to.setHours(23, 59, 59, 999);
            return { from, to };
        }
        case 'last_week': {
            const from = new Date(now);
            from.setDate(now.getDate() - dayOfWeek - 7);
            from.setHours(0, 0, 0, 0);
            const to = new Date(from);
            to.setDate(from.getDate() + 6);
            to.setHours(23, 59, 59, 999);
            return { from, to };
        }
        case 'current_month': {
            const from = new Date(y, m, 1, 0, 0, 0, 0);
            const to = new Date(y, m + 1, 0, 23, 59, 59, 999);
            return { from, to };
        }
        case 'last_month': {
            const from = new Date(y, m - 1, 1, 0, 0, 0, 0);
            const to = new Date(y, m, 0, 23, 59, 59, 999);
            return { from, to };
        }
        default:
            return null;
    }
}

function toInputDate(date) {
    // Returns YYYY-MM-DD for <input type="date">
    return date.toISOString().slice(0, 10);
}

export default function AdminPortalTmaReport() {
    // ── Step 1: Customer search ──────────────────────────────────────────
    const [customerQuery, setCustomerQuery] = useState('');
    const debouncedCustomerQuery = useDebounce(customerQuery, 350);
    const [customers, setCustomers] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [customersError, setCustomersError] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    // ── Step 2: Date range ───────────────────────────────────────────────
    const [dateRangeKey, setDateRangeKey] = useState(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // ── Step 3: TMA devices for selected customer ────────────────────────
    const [devices, setDevices] = useState([]);
    const [devicesLoading, setDevicesLoading] = useState(false);
    const [devicesError, setDevicesError] = useState('');

    // ── Step 4: Device selection ─────────────────────────────────────────
    const [selectedDeviceIds, setSelectedDeviceIds] = useState(new Set());

    // ── Customer search fetch ─────────────────────────────────────────────
    useEffect(() => {
        if (!debouncedCustomerQuery.trim()) {
            setCustomers([]);
            setShowDropdown(false);
            return;
        }

        let cancelled = false;
        setCustomersLoading(true);
        setCustomersError('');

        get('/customers', { nameContains: debouncedCustomerQuery })
            .then(res => {
                if (cancelled) return;
                setCustomers(Array.isArray(res.results) ? res.results : []);
                setShowDropdown(true);
            })
            .catch(() => {
                if (cancelled) return;
                setCustomersError('Failed to search customers.');
            })
            .finally(() => {
                if (!cancelled) setCustomersLoading(false);
            });

        return () => { cancelled = true; };
    }, [debouncedCustomerQuery]);

    // ── Close dropdown on outside click ──────────────────────────────────
    useEffect(() => {
        function handleClick(e) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // ── Load TMA devices when customer is selected ────────────────────────
    useEffect(() => {
        if (!selectedCustomer) {
            setDevices([]);
            setSelectedDeviceIds(new Set());
            return;
        }

        let cancelled = false;
        setDevicesLoading(true);
        setDevicesError('');
        setDevices([]);
        setSelectedDeviceIds(new Set());

        get(`/deviceslist?customer_id=${selectedCustomer.id}&active=true`)
            .then(res => {
                if (cancelled) return;
                const all = Array.isArray(res.results) ? res.results : (Array.isArray(res) ? res : []);
                const tma = all.filter(d => d.devicetype_id === SENTINEL_TMA);
                setDevices(tma);
            })
            .catch(() => {
                if (cancelled) return;
                setDevicesError('Failed to load devices for this customer.');
            })
            .finally(() => {
                if (!cancelled) setDevicesLoading(false);
            });

        return () => { cancelled = true; };
    }, [selectedCustomer]);

    // ── Handlers ──────────────────────────────────────────────────────────
    function handleSelectCustomer(customer) {
        setSelectedCustomer(customer);
        setCustomerQuery(customer.name);
        setShowDropdown(false);
    }

    function handleClearCustomer() {
        setSelectedCustomer(null);
        setCustomerQuery('');
        setDevices([]);
        setSelectedDeviceIds(new Set());
    }

    function toggleDevice(id) {
        setSelectedDeviceIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }

    function handleDatePreset(key) {
        const range = getDateRange(key);
        setDateRangeKey(key);
        setFromDate(toInputDate(range.from));
        setToDate(toInputDate(range.to));
    }

    function handleFromDateChange(val) {
        setFromDate(val);
        setDateRangeKey(null);
    }

    function handleToDateChange(val) {
        setToDate(val);
        setDateRangeKey(null);
    }

    function selectAll() {
        setSelectedDeviceIds(new Set(devices.map(d => d.id)));
    }

    function deselectAll() {
        setSelectedDeviceIds(new Set());
    }

    const selectedCount = selectedDeviceIds.size;
    const dateRangeValid = fromDate && toDate && fromDate <= toDate;
    const [showSummary, setShowSummary] = useState(false);

    const selectedDevices = devices.filter(d => selectedDeviceIds.has(d.id));

    if (showSummary) {
        return (
            <AdminPortalTmaReportSummary
                customer={selectedCustomer}
                devices={selectedDevices}
                fromDate={fromDate}
                toDate={toDate}
                onBack={() => setShowSummary(false)}
            />
        );
    }

    return (
        <>
            <FrameHeader title="TMA Report" />

            {/* ── Step 1: Customer Lookup ── */}
            <Frame>
                <div className={styles.stepLabel}>Step 1 — Select customer</div>

                <div className={styles.customerSearch} ref={dropdownRef}>
                    <div className={styles.customerInputRow}>
                        <TextInput
                            value={customerQuery}
                            placeholder="Search by customer name…"
                            icon="search"
                            loading={customersLoading}
                            onChange={e => {
                                setCustomerQuery(e.target.value);
                                if (selectedCustomer) setSelectedCustomer(null);
                            }}
                            onFocus={() => customers.length > 0 && setShowDropdown(true)}
                        />
                        {selectedCustomer && (
                            <Button onClick={handleClearCustomer}>Clear</Button>
                        )}
                    </div>

                    {customersError && (
                        <div className={styles.error}>{customersError}</div>
                    )}

                    {showDropdown && customers.length > 0 && (
                        <div className={styles.dropdown}>
                            {customers.map(c => (
                                <div
                                    key={c.id}
                                    className={styles.dropdownItem}
                                    onMouseDown={() => handleSelectCustomer(c)}
                                >
                                    <span className={styles.customerName}>{c.name}</span>
                                    {c.email && (
                                        <span className={styles.customerEmail}>{c.email}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {showDropdown && !customersLoading && customers.length === 0 && customerQuery.trim() && (
                        <div className={styles.dropdown}>
                            <div className={styles.noResults}>No customers found</div>
                        </div>
                    )}
                </div>

                {selectedCustomer && (
                    <div className={styles.selectedBadge}>
                        <span className={styles.selectedBadgeIcon}>✓</span>
                        Customer selected: <strong>{selectedCustomer.name}</strong>
                    </div>
                )}
            </Frame>

            {/* ── Step 2: Date range ── */}
            {selectedCustomer && (
                <Frame>
                    <div className={styles.stepLabel}>Step 2 — Select date range</div>
                    <div className={styles.dateRangeGroup}>
                        {DATE_RANGE_OPTIONS.map(opt => (
                            <button
                                key={opt.key}
                                className={`${styles.dateRangeBtn} ${dateRangeKey === opt.key ? styles.dateRangeBtnActive : ''}`}
                                onClick={() => handleDatePreset(opt.key)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    <div className={styles.dateInputRow}>
                        <div className={styles.dateInputGroup}>
                            <label className={styles.dateInputLabel}>From</label>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={fromDate}
                                onChange={e => handleFromDateChange(e.target.value)}
                            />
                        </div>
                        <span className={styles.dateInputSep}>—</span>
                        <div className={styles.dateInputGroup}>
                            <label className={styles.dateInputLabel}>To</label>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={toDate}
                                min={fromDate || undefined}
                                onChange={e => handleToDateChange(e.target.value)}
                            />
                        </div>
                    </div>
                    {fromDate && toDate && fromDate > toDate && (
                        <div className={styles.error}>End date must be after start date.</div>
                    )}
                </Frame>
            )}

            {/* ── Step 3 & 4: TMA Device List with selection ── */}
            {selectedCustomer && dateRangeValid && (
                <Frame>
                    <div className={styles.stepLabel}>
                        Step 3 — Select TMA Mobile devices to include
                    </div>

                    {devicesLoading && <LoadingFrame />}

                    {devicesError && (
                        <div className={styles.error}>{devicesError}</div>
                    )}

                    {!devicesLoading && !devicesError && devices.length === 0 && (
                        <div className={styles.emptyDevices}>
                            No TMA Mobile devices found for <strong>{selectedCustomer.name}</strong>.
                        </div>
                    )}

                    {!devicesLoading && devices.length > 0 && (
                        <>
                            <div className={styles.deviceListHeader}>
                                <span className={styles.deviceCount}>
                                    {devices.length} TMA Mobile device{devices.length !== 1 ? 's' : ''} found
                                    {selectedCount > 0 && ` · ${selectedCount} selected`}
                                </span>
                                <div className={styles.bulkActions}>
                                    <button
                                        className={styles.linkButton}
                                        onClick={selectAll}
                                        disabled={selectedCount === devices.length}
                                    >
                                        Select all
                                    </button>
                                    <span className={styles.separator}>·</span>
                                    <button
                                        className={styles.linkButton}
                                        onClick={deselectAll}
                                        disabled={selectedCount === 0}
                                    >
                                        Deselect all
                                    </button>
                                </div>
                            </div>

                            <div className={styles.deviceTable}>
                                <div className={styles.headerRow}>
                                    <div />
                                    <div>Serial / Name</div>
                                    <div>Serial number</div>
                                    <div>Status</div>
                                    <div>Active</div>
                                </div>

                                {devices.map(d => {
                                    const checked = selectedDeviceIds.has(d.id);
                                    return (
                                        <label
                                            key={d.id}
                                            className={`${styles.deviceRow} ${checked ? styles.checked : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                className={styles.checkbox}
                                                checked={checked}
                                                onChange={() => toggleDevice(d.id)}
                                            />
                                            <div className={styles.deviceName}>
                                                {d.name || d.serial_number || `Device #${d.id}`}
                                            </div>
                                            <div className={styles.cell}>
                                                {d.serial_number || '—'}
                                            </div>
                                            <div className={styles.cell}>
                                                <span className={`${styles.statusBadge} ${getStatusClass(d.status)}`}>
                                                    {d.status || 'Unknown'}
                                                </span>
                                            </div>
                                            <div className={styles.cell}>
                                                <span className={d.active ? styles.activeYes : styles.activeNo}>
                                                    {d.active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>

                            <div className={styles.footer}>
                                <Button
                                    theme="primary"
                                    disabled={selectedCount === 0}
                                    onClick={() => setShowSummary(true)}
                                >
                                    {selectedCount === 0
                                        ? 'Select devices to continue'
                                        : `Generate report for ${selectedCount} device${selectedCount !== 1 ? 's' : ''}`}
                                </Button>
                            </div>
                        </>
                    )}
                </Frame>
            )}
        </>
    );
}

function getStatusClass(status) {
    if (!status) return '';
    const s = status.toUpperCase();
    if (s === 'OK' || s === 'READY' || s === 'DEPLOYED') return styles.statusOk;
    if (s.includes('WARNING') || s.includes('MAINTENANCE') || s.includes('INSPECTION')) return styles.statusWarning;
    if (s.includes('ERROR') || s.includes('CRITICAL') || s.includes('INACTIVE') || s.includes('EXPIRED')) return styles.statusError;
    return styles.statusInfo;
}
